import { useState, useEffect } from 'react';
import { Box, Button, styled, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { getTheme, spacingSizeMap } from '@insights-ltd/design-library/themes';
import { SvgShareExternalLinkCustom } from '@insights-ltd/design-library/components';

type LinkDisplayProps = {
  id?: string;
  link: string;
  linkCopied?: boolean;
  onCopy?: (id: string) => void;
  name?: string;
};

const StyledLink = styled(Link)({
  marginLeft: '0.25rem',
});

const LinkDisplay = ({
  id,
  link,
  linkCopied,
  onCopy,
  name,
}: LinkDisplayProps) => {
  const localTheme = getTheme();
  const { t } = useTranslation();

  const copy = t(
    'ui.event-management.evaluator-links.completed-evaluators.link.button.copy',
  );
  const copied = t(
    'ui.event-management.evaluator-links.completed-evaluators.link.button.copied',
  );

  const [isCopied, setIsCopied] = useState<boolean>(!!linkCopied);

  const handleCopy = async () => {
    navigator.clipboard.writeText(link).then();
    if (!isCopied) {
      setIsCopied(true);
      if (onCopy && id) {
        onCopy(id);
      }
    }
  };

  useEffect(() => {
    setIsCopied(!!linkCopied);
  }, [copied, copy, linkCopied]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={(theme) => ({
          marginBottom: theme.spacing(spacingSizeMap.S),
          fontWeight: theme.typography.fontWeightBold,
        })}
      >
        <span>
          {t(
            'ui.event-management.evaluator-links.completed-evaluators.link.label',
          )}
        </span>
        <StyledLink data-testid="external-link-icon" to={link} target="_blank">
          <SvgShareExternalLinkCustom
            height={14}
            stroke={localTheme.brandColours.blue}
            strokeWidth={2}
          />
        </StyledLink>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
        }}
      >
        <TextField
          sx={(theme) => ({
            backgroundColor: theme.palette.grey[100],
            fieldset: {
              borderColor: `${theme.palette.grey[600]} !important`,
            },
          })}
          variant="outlined"
          defaultValue={link}
          onClick={() => handleCopy()}
          disabled
          slotProps={{
            htmlInput: {
              readOnly: true,
              style: {
                width: '534px',
                height: '48px',
                padding: '0 14px',
                cursor: 'pointer',
              },
              'data-testid': name,
            },
          }}
        />
        <Button
          sx={(theme) => ({
            maxWidth: '100px',
            maxHeight: '48px',
            minWidth: '100px',
            minHeight: '48px',
            width: '100%',
            height: '100%',
            margin: `0 ${theme.spacing(spacingSizeMap.S)}`,
            padding: '0 !important',
            color: theme.palette.blue.main,
            border: `2px solid ${theme.palette.blue.main}`,
          })}
          variant="outlined"
          onClick={() => handleCopy()}
        >
          {isCopied ? copied : copy}
        </Button>
      </Box>
    </Box>
  );
};

export default LinkDisplay;
